import {
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
} from '@chakra-ui/react';
import * as React from 'react';
import {
  SearchBoxProvided,
  StateResultsProvided,
} from 'react-instantsearch-core';
import { connectSearchBox, connectStateResults } from 'react-instantsearch-dom';

import { generateTrackingId, search_query_submitted } from '@/analytics';
import { SearchIcon } from '@/imports/ui/chakra/feather';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';

type SearchBoxWithSearchStateProps = StateResultsProvided &
  Pick<SearchBoxProvided, 'refine'> & {
    placeholder?: string;
  };

const SearchBoxWithSearchState =
  connectStateResults<SearchBoxWithSearchStateProps>(
    ({ refine, isSearchStalled, placeholder, searching }) => {
      const [value, setValue] = React.useState('');
      const debouncedValue = useDebouncedValue(value, 500);
      const previousQueryRef = React.useRef('');

      React.useEffect(() => {
        refine(debouncedValue);

        if (debouncedValue && debouncedValue !== previousQueryRef.current) {
          search_query_submitted({
            entity: {
              id: generateTrackingId(),
              type: 'search_query',
            },
            search: {
              query: debouncedValue,
              searchType: 'global',
              resultCount: 0,
              hasResults: true,
              queryLength: debouncedValue.length,
            },
          });
          previousQueryRef.current = debouncedValue;
        }
      }, [refine, debouncedValue]);

      const isDebouncing = Boolean(value.trim()) && value !== debouncedValue;
      const showLoader =
        isDebouncing ||
        searching ||
        (Boolean(debouncedValue.trim()) && isSearchStalled);

      return (
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.600" />
          </InputLeftElement>
          <Input
            variant="filled"
            borderRadius="full"
            placeholder={placeholder}
            type="search"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            aria-busy={showLoader}
            pr={showLoader ? 10 : undefined}
          />
          {showLoader ? (
            <InputRightElement pointerEvents="none">
              <Spinner
                size="sm"
                color="blue.500"
                thickness="3px"
                speed="0.65s"
              />
            </InputRightElement>
          ) : null}
        </InputGroup>
      );
    }
  );

export const SearchBox = connectSearchBox<
  SearchBoxProvided & { placeholder?: string }
>(({ refine, placeholder }) => (
  <SearchBoxWithSearchState refine={refine} placeholder={placeholder} />
));
